/**
 * Toast — lightweight in-app notification system.
 *
 * Usage:
 *   import { useToast, ToastContainer } from '../components/Toast.jsx'
 *
 *   // In your component:
 *   const toast = useToast()
 *   toast.error('Could not save wine.')
 *   toast.success('Wine saved!')
 *   toast.info('Copied to clipboard.')
 *
 *   // In App.jsx (once, at the root):
 *   <ToastContainer />
 *
 * The hook reads/writes a module-level store so no React context is needed.
 * ToastContainer subscribes via a listener and renders the queue.
 */

import { useState, useEffect } from 'react'

// ── Store ─────────────────────────────────────────────────────────────────────
let _id       = 0
let _toasts   = []
let _listeners = []

function notify() {
  _listeners.forEach(fn => fn([..._toasts]))
}

function addToast(message, type = 'info', duration = 3500) {
  const id = ++_id
  _toasts = [..._toasts, { id, message, type }]
  notify()
  setTimeout(() => removeToast(id), duration)
}

function removeToast(id) {
  _toasts = _toasts.filter(t => t.id !== id)
  notify()
}

export function clearAllToasts() {
  _toasts = []
  notify()
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useToast() {
  return {
    success: (msg, duration) => addToast(msg, 'success', duration),
    error:   (msg, duration) => addToast(msg, 'error',   duration ?? 5000),
    info:    (msg, duration) => addToast(msg, 'info',    duration),
  }
}

// ── Container ─────────────────────────────────────────────────────────────────
//
// All three states are now brand-aligned (gold/burgundy/cellar). Success
// previously used a mint-green gradient + light-green checkmark which read
// off-brand against everything else in the app. The success state now uses
// gold (the natural brand "go / yes / saved" color) on a cellar gradient,
// matching the rest of the visual language.
const STYLES = {
  success: {
    background: 'linear-gradient(135deg, rgba(58,13,24,0.97), rgba(14,12,18,0.97))',
    border: '1px solid rgba(196,150,90,0.45)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="rgba(196,150,90,0.7)" strokeWidth="1.5"/>
        <path d="M7.5 12l3 3 6-6" stroke="rgba(196,150,90,0.95)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    color: 'rgba(244,239,231,0.95)',
  },
  error: {
    background: 'linear-gradient(135deg, rgba(58,13,24,0.97), rgba(14,12,18,0.97))',
    border: '1px solid rgba(248,113,113,0.5)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="rgba(248,113,113,0.85)" strokeWidth="1.5"/>
        <path d="M12 7v5" stroke="rgba(248,113,113,0.95)" strokeWidth="1.8" strokeLinecap="round"/>
        <circle cx="12" cy="16.5" r="1" fill="rgba(248,113,113,0.95)"/>
      </svg>
    ),
    color: 'rgba(244,239,231,0.95)',
  },
  info: {
    background: 'linear-gradient(135deg, rgba(58,13,24,0.97), rgba(14,12,18,0.97))',
    border: '1px solid rgba(196,150,90,0.3)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="rgba(196,150,90,0.65)" strokeWidth="1.5"/>
        <line x1="12" y1="8" x2="12" y2="12" stroke="rgba(196,150,90,0.85)" strokeWidth="1.8" strokeLinecap="round"/>
        <circle cx="12" cy="16" r="1" fill="rgba(196,150,90,0.85)"/>
      </svg>
    ),
    color: 'rgba(244,239,231,0.92)',
  },
}

function ToastItem({ toast, onDismiss }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Trigger enter animation on next tick
    const t = setTimeout(() => setVisible(true), 10)
    return () => clearTimeout(t)
  }, [])

  const style = STYLES[toast.type] || STYLES.info

  return (
    <div
      onClick={() => onDismiss(toast.id)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 16px',
        borderRadius: 12,
        background: style.background,
        border: style.border,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        cursor: 'pointer',
        maxWidth: 360,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        opacity: visible ? 1 : 0,
        transition: 'transform 0.25s ease, opacity 0.25s ease',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ flexShrink: 0 }}>{style.icon}</div>
      <p style={{
        fontFamily: 'Raleway, sans-serif',
        fontSize: 13,
        color: style.color,
        margin: 0,
        lineHeight: 1.5,
        flex: 1,
      }}>
        {toast.message}
      </p>
    </div>
  )
}

export function ToastContainer() {
  const [toasts, setToasts] = useState([])

  useEffect(() => {
    const handler = (next) => setToasts(next)
    _listeners.push(handler)
    return () => { _listeners = _listeners.filter(l => l !== handler) }
  }, [])

  if (toasts.length === 0) return null

  return (
    <div style={{
      position: 'fixed',
      // VIN-2026-87: clear the bottom nav AND the home-indicator safe area —
      // a flat bottom:90 let toasts clip into the safe area on notch devices.
      bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))',   // above the bottom nav
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      alignItems: 'center',
      pointerEvents: 'none',
      width: '100%',
      padding: '0 20px',
    }}>
      {toasts.map(toast => (
        <div key={toast.id} style={{ pointerEvents: 'auto', width: '100%', maxWidth: 360 }}>
          <ToastItem toast={toast} onDismiss={removeToast} />
        </div>
      ))}
    </div>
  )
}
